import React, { PropsWithChildren, useEffect, useContext, useCallback, useMemo, useState } from "react";
import { useGetInitialAppData } from "./api/useGetInitialAppData";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import PageContext from "../../components/Common/PageContext";
import { AllowedLocationResponse } from "../../common/api/Models/AllowedLocationResponse";
import { AppDataStore, InitialAppDataContext } from "./InitialAppDataContext";
import { UserContext } from "../user";
import GetRandomTagLine from "../../util/TagLines";
import Splash from "../../components/Common/Splash";
import ReceivingAppIcon from "../../icons/ReceivingAppIcon";
import { LocationSummaryResponse } from "../../common/api/Models/LocationSummaryResponse";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { useBadgeCount } from "./api/useBadgeCount";

export const InitialAppDataProvider: React.FC<PropsWithChildren<any>> = ({ children }) => {
	const [{ data: appData, loading: isLoading, error: apiError }, refreshData] = useGetInitialAppData();
	const [pageContext, updatePageContext] = useContext(PageContext);
	const { graphProfile } = useContext(UserContext);
	const { userLocation, userInformation } = pageContext;
	const [firstLoad, setFirstLoad] = useState(true);
	/* appDataLoaded informs us when app data loaded and context updated */
	const [appDataLoaded, setAppDataLoaded] = useState(false);

	const [showErrorAlert, setShowErrorAlert] = React.useState(false);
	const { badgeCount } = useBadgeCount(userLocation);

	useEffect(() => {
		if (badgeCount) {
			updatePageContext({ badge: badgeCount });
		}
	}, [badgeCount, updatePageContext]);

	const changeLocation = useCallback(
		async (value: string, countryCode: string) => {
			updatePageContext({ userLocation: parseInt(value, 10) });
			try {
				await refreshData({ params: { countryCode, physicalLocationId: value } });
			} catch (error) {
				console.error("Error changing location", error);
			}
		},
		[refreshData, updatePageContext]
	);

	const changeTenantCountry = useCallback(
		async (value: string) => {
			updatePageContext({ userInformation: { ...userInformation, countryCode: value } });
			try {
				await refreshData({ params: { countryCode: value } });
			} catch (error) {
				console.error("Error changing tenant country", error);
			}
		},
		[refreshData, updatePageContext, userInformation]
	);

	useEffect(() => {
		if (apiError) {
			setShowErrorAlert(true);
			return;
		}

		if (!appData) return;

		type LocationState = {
			authorised: { ids: number[]; primary: number; list: AllowedLocationResponse[] };
			userCurrentLocation: number;
			allLocations: LocationSummaryResponse[];
			physical: { [key: string]: unknown };
			default: number;
			isLoaded: boolean;
		};

		type PrinterState = {
			printers: any[];
			printer: any;
			selectedPrinterId: string;
		};

		function mapLocationData(appData: any): LocationState {
			const locations: LocationState = {
				authorised: { ids: [], primary: -1, list: [] },
				userCurrentLocation: 1,
				allLocations: [],
				physical: {},
				default: 0,
				isLoaded: true,
			};
			locations.authorised.list = appData.userAllowedLocations;
			locations.userCurrentLocation = appData.userCurrentLocation?.id ?? 1;
			locations.allLocations = appData.allLocations;
			return locations;
		}

		function mapPrinterData(appData: any): PrinterState {
			const defaultPrinterName = "STOREROOM-PLAIN";
			const printer = appData.currentAvailablePrinters.find((printer: any) => printer.name === defaultPrinterName);

			return {
				printers: appData.currentAvailablePrinters,
				printer: printer || {},
				selectedPrinterId: printer?.id ?? "",
			};
		}

		const printerState = mapPrinterData(appData);
		const locationState = mapLocationData(appData);
		const putAwayLocationsSet = new Set(appData.putAwayLocations);

		updatePageContext({
			userAllowedLocations: appData.userAllowedLocations,
			userLocation: locationState.userCurrentLocation,
			fulfilmentCentreLocations: appData.fulfilmentCentreLocations,

			printers: printerState.printers,
			printer: printerState.printer,
			selectedPrinterId: printerState.selectedPrinterId,

			userInformation: appData.userInformation,
			productGroups: appData.productGroups,
			creditRequestReasons: appData.creditRequestReasons,
			locations: locationState,
			featureConfiguration: appData.featureConfiguration,
			carriers: appData.carriers,
			putAwayLocations: putAwayLocationsSet,
			commercialAccounts: appData.commercialAccounts,
			taxRate: appData.taxRate ?? 0,
		});

		setFirstLoad(false);
		setAppDataLoaded(true);
	}, [apiError, appData, graphProfile.storeNumber, updatePageContext]);

	const handleCloseAlert = () => {
		setShowErrorAlert(false);
	};

	const providerValue: AppDataStore = useMemo(() => {
		if (isLoading || !appData) {
			return {
				appData: undefined,
				refreshData,
				changeLocation,
				changeTenantCountry,
				isLoading: true,
			};
		}

		return {
			appData: appData,
			refreshData,
			changeLocation,
			changeTenantCountry,
			isLoading: false,
		};
	}, [appData, changeLocation, changeTenantCountry, isLoading, refreshData]);

	return (
		<>
			{isLoading && (
				<>
					{firstLoad && <Splash title="Receiving App" icon={<ReceivingAppIcon />} secondaryText={GetRandomTagLine()} />}
					{!firstLoad && <LoadingDialog message="Application loading" isOpen={true} />}
				</>
			)}
			{showErrorAlert && (
				<ModalWithActionButton
					title="Error"
					alertType="error"
					primaryButtonName="Ok"
					onPrimaryButtonClick={handleCloseAlert}
					isOpen={true}
					message={
						"We encountered trouble getting the initial application data for you. Please refresh the page to try again."
					}
				/>
			)}
			{appDataLoaded && (
				<InitialAppDataContext.Provider value={providerValue}>{children}</InitialAppDataContext.Provider>
			)}
		</>
	);
};
